import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { getWorksheet, gradeWorksheet, aiExplain, aiHint, emailWorksheetToTeacher } from "@/lib/api";
import { Loader2, Printer, RefreshCw, BrainCircuit, Check, X, Sparkles, Lightbulb, Send, Mail, Volume2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { QuestionText } from "@/components/QuestionText";
import MathText from "@/components/MathText";
import { WorksheetHeader, InstructionRibbon, SectionBanner, WorksheetFooter } from "@/components/WorksheetTheme";
import { buildWorksheetPdfBase64 } from "@/lib/worksheetImage";
import { useAuth } from "@/lib/auth";
import ReportProblem from "@/components/ReportProblem";

const PRINT_CHOICE_LABELS = ["A", "B", "C", "D", "E", "F"];

// Static, print-styled snapshot of the worksheet with the student's answers baked
// in. Captured off-screen by the same html2canvas → jsPDF pipeline the generator's
// Print button uses, so the output matches that PDF (clean card, numbered items,
// per-item pagination). No interactive controls — selections are drawn statically.
// Section title + instructions for a single-skill deterministic worksheet, chosen
// from whether its problems are multiple-choice or free-response.
function deterministicSection(problems) {
  const withChoices = problems.filter((p) => p.choices && p.choices.length).length;
  if (withChoices === problems.length) {
    return { title: "Multiple Choice", instructions: "Choose the letter of the correct answer." };
  }
  if (withChoices === 0) {
    return { title: "Solve Each Item", instructions: "Write your answer on the line provided." };
  }
  return { title: "Answer Each Item", instructions: "Choose or write the correct answer for each item." };
}

function PracticePrintLayout({ printRef, ws, answers, result, meta }) {
  const META = [
    ["name", "Name"],
    ["date", "Date"],
    ["gradeSection", "Grade & Section"],
    ["subject", "Subject"],
  ];
  const sec = deterministicSection(ws.problems);
  const gradeLabel = ws.grade === "K" ? "Kindergarten" : `Grade ${ws.grade}`;
  return (
    <div className="fixed left-[-100000px] top-0 w-[760px] print:static print:w-auto" aria-hidden="true">
      <div ref={printRef} className="bg-white p-8 print-page">
        <WorksheetHeader
          title={ws.title}
          eyebrow="Worksheet"
          subtitle={`${gradeLabel} · ${ws.problems.length} ${ws.problems.length === 1 ? "item" : "items"}`}
        />
        <InstructionRibbon>Read each item carefully and answer to the best of your ability.</InstructionRibbon>
        <div className="grid grid-cols-2 gap-x-10 gap-y-3 text-sm border-y-[3px] border-[#0A0A0A]/10 py-3 mb-4">
          {META.map(([k, label]) => (
            <span key={k} className="font-bold inline-flex items-baseline gap-2">
              <span className="shrink-0">{label}:</span>
              <span className="inline-block flex-1 min-w-[8rem] border-b-2 border-[#0A0A0A]/55">{meta?.[k] || " "}</span>
            </span>
          ))}
          <span className="font-bold inline-flex items-baseline gap-2">
            <span className="shrink-0">Score:</span>
            <span className="inline-block min-w-[7rem] border-b-2 border-[#0A0A0A]/55 font-bold" data-print-lift>
              {result ? `${result.score} / ${result.total}` : " "}
            </span>
          </span>
        </div>

        <div>
          <section>
            <SectionBanner index={0} title={sec.title} points={ws.problems.length} instructions={sec.instructions} />
            <ol className="grid gap-6 list-none p-0 m-0">
              {ws.problems.map((p, i) => {
                const userVal = answers[p.id] || "";
                return (
                  <li key={p.id} className="break-inside-avoid">
                    <div className="flex items-start gap-3">
                      <span className="shrink-0 w-8 h-8 rounded-full bg-brand-yellow border-[3px] border-[#0A0A0A] flex items-center justify-center font-bold text-sm">
                        {i + 1}
                      </span>
                      <div className="grid gap-2 w-full">
                        <div className="font-bold">
                          <QuestionText text={p.question} size="base" />
                        </div>
                        {p.figure_svg && (
                          <div className="my-1" dangerouslySetInnerHTML={{ __html: p.figure_svg }} />
                        )}
                        {p.choices ? (
                          <ul className="grid grid-cols-1 gap-1.5">
                            {p.choices.map((choice, ci) => {
                              const chosen = userVal === choice;
                              return (
                                <li
                                  key={ci}
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 text-sm ${
                                    chosen ? "border-[#0A0A0A] bg-brand-yellow/40" : "border-[#0A0A0A]/15 bg-[#F9F9F6]"
                                  }`}
                                >
                                  <span className="font-bold bg-white border-2 border-[#0A0A0A] rounded-md w-[22px] h-[22px] flex items-center justify-center text-xs shrink-0">
                                    {PRINT_CHOICE_LABELS[ci] ?? ci + 1}
                                  </span>
                                  <span><MathText>{choice}</MathText></span>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <div className="border-2 border-[#0A0A0A] rounded-lg px-3 py-2 text-sm min-h-[1.5em] max-w-sm whitespace-pre-wrap break-words">
                            {userVal}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        </div>

        <WorksheetFooter />
      </div>
    </div>
  );
}

function HintBlock({ hint, aiHintText, aiHintLoading, onAskAi }) {
  const [open, setOpen] = useState(false);
  // Fetch the AI hint the moment the hint is opened — no extra click. The static
  // `hint` is only ever used as a fallback if the AI call fails.
  useEffect(() => {
    if (open && !aiHintText && !aiHintLoading) onAskAi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!hint) return null;
  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0A0A0A] underline underline-offset-2 hover:text-brand-blue"
        data-testid="hint-toggle"
      >
        <Lightbulb strokeWidth={3} className="w-3.5 h-3.5" />
        {open ? "Hide hint" : "Show hint"}
      </button>
      {open && (
        <div className="mt-2 p-3 bg-brand-yellow/40 border-2 border-dashed border-[#0A0A0A] rounded-lg text-sm font-semibold" data-testid="hint-content">
          {aiHintLoading && !aiHintText ? (
            <span className="inline-flex items-center gap-1.5 font-normal text-muted-foreground">
              <Loader2 strokeWidth={3} className="w-3.5 h-3.5 animate-spin" /> Thinking of a hint…
            </span>
          ) : (
            <>💡 <MathText>{aiHintText || hint}</MathText></>
          )}
        </div>
      )}
    </div>
  );
}


function Figure({ svg }) {
  if (!svg) return null;
  return (
    <div
      className="my-3 p-3 bg-[#F9F9F6] border-[3px] border-[#0A0A0A] rounded-xl block w-full"
      data-testid="figure"
      // Inline SVG is generated server-side from a small fixed template; safe to render.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// "Listen" button for skills that give the student a word/number to hear
// (e.g. Pre-K "Choose the number that you hear") — uses the browser's
// built-in speech synthesis, no audio files or backend involved. Feature-
// detects and renders nothing if the browser doesn't support it; the word
// is still visible in the question text as a fallback either way.
function ListenButton({ text }) {
  const [speaking, setSpeaking] = useState(false);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  if (!supported || !text) return null;
  const onClick = () => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = 0.85;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="brutal-btn bg-brand-blue text-white px-3 py-2 text-xs inline-flex items-center gap-1.5 mb-3"
      data-testid="listen-button"
    >
      <Volume2 strokeWidth={3} className={`w-3.5 h-3.5 ${speaking ? "animate-pulse" : ""}`} />
      {speaking ? "Playing…" : "Listen"}
    </button>
  );
}

const CHOICE_LABELS = ["A", "B", "C", "D"];

/** Numpad: shown when choices are the numbers 1–10 (K counting questions) */
function NumpadChoices({ choices, value, onChange, disabled, result }) {
  const rows = [choices.slice(0, 5), choices.slice(5)];
  return (
    <div className="mt-5 space-y-3" data-testid="mc-choices">
      {rows.map((row, ri) => (
        <div key={ri} className="flex gap-3">
          {row.map((choice) => {
            const isSelected = value === choice;
            const isCorrect  = result && result.correct_answer === choice;
            const isWrong    = result && isSelected && !result.is_correct;
            let cls = "bg-[#2563EB] text-white border-[#1d4ed8]";
            if (isCorrect && result) cls = "bg-brand-green text-[#0A0A0A] border-green-600 scale-110";
            else if (isWrong)        cls = "bg-brand-coral text-white border-red-500";
            else if (isSelected)     cls = "bg-[#1d4ed8] text-white border-[#1e40af] scale-110";
            return (
              <button
                key={choice}
                onClick={() => !disabled && onChange(choice)}
                disabled={disabled}
                data-testid={`mc-choice-${choice}`}
                className={`w-13 h-13 w-[3.25rem] h-[3.25rem] rounded-full font-bold text-lg border-[3px] flex items-center justify-center shadow-brutal-xs transition-transform hover:scale-110 active:scale-95 ${cls} ${disabled ? "cursor-default" : ""}`}
              >
                {choice}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function MultipleChoice({ choices, value, onChange, disabled, result }) {
  const isNumpad = choices.length === 10 && choices.every(c => /^\d+$/.test(c.trim()));
  if (isNumpad) {
    return <NumpadChoices choices={choices} value={value} onChange={onChange} disabled={disabled} result={result} />;
  }
  return (
    <div className="mt-4 grid grid-cols-1 gap-2.5" data-testid="mc-choices">
      {choices.map((choice, idx) => {
        const label = CHOICE_LABELS[idx] || String(idx + 1);
        const isSelected = value === choice;
        const isCorrect = result && result.correct_answer === choice;
        const isWrong = result && isSelected && !result.is_correct;
        let bg = "bg-white";
        if (isCorrect && result) bg = "bg-brand-green";
        else if (isWrong) bg = "bg-brand-coral";
        else if (isSelected) bg = "bg-brand-yellow";
        return (
          <button
            key={idx}
            onClick={() => !disabled && onChange(choice)}
            disabled={disabled}
            data-testid={`mc-choice-${label}`}
            className={`brutal-btn text-left px-4 py-3 flex items-center gap-3 ${bg} ${disabled ? "cursor-default hover:translate-y-0 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]" : ""}`}
          >
            <span className={`w-8 h-8 flex-shrink-0 border-[2.5px] border-[#0A0A0A] rounded-lg flex items-center justify-center font-heading font-bold text-sm ${isSelected ? "bg-[#0A0A0A] text-white" : "bg-white"}`}>
              {label}
            </span>
            <span className="font-mono font-semibold text-sm leading-snug flex-1"><MathText>{choice}</MathText></span>
            {isCorrect && result && <Check strokeWidth={3} className="w-4 h-4 flex-shrink-0" />}
            {isWrong && <X strokeWidth={3} className="w-4 h-4 flex-shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}

export default function Practice() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ws, setWs] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [explanations, setExplanations] = useState({});
  const [explainingId, setExplainingId] = useState(null);
  const [aiHints, setAiHints] = useState({});
  const [hintLoadingId, setHintLoadingId] = useState(null);
  const printRef = useRef(null);
  // Interactive student header fields (typed directly on the worksheet).
  const [meta, setMeta] = useState({ name: "", date: "", gradeSection: "", subject: "" });
  const setMetaField = (k, v) => setMeta((m) => ({ ...m, [k]: v }));
  // Submit-to-Teacher modal state.
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [teacherEmail, setTeacherEmail] = useState("");
  const [sending, setSending] = useState(false);

  // Print = the browser's native print dialog. @media print CSS hides the
  // interactive app and shows PracticePrintLayout (the student's filled-out
  // worksheet) instead, so the browser paginates real vector text — same crisp
  // output as the /print/:id "Print / Save PDF" flow, per the site owner's
  // preference over the rasterized html2canvas PDF (which is kept only for the
  // teacher email, where an actual file attachment is required).
  const onPrint = () => window.print();

  useEffect(() => {
    getWorksheet(id).then(setWs).catch(() => toast.error("Worksheet not found"));
  }, [id, user]);

  const onSubmit = async () => {
    if (!ws) return;
    const unanswered = ws.problems.filter((p) => !answers[p.id]).length;
    if (unanswered > 0) {
      const ok = window.confirm(`You have ${unanswered} unanswered question${unanswered > 1 ? "s" : ""}. Submit anyway?`);
      if (!ok) return;
    }
    const payload = ws.problems.map((p) => ({ problem_id: p.id, user_answer: answers[p.id] || "" }));
    setSubmitting(true);
    try {
      const r = await gradeWorksheet(ws.id, payload);
      setResult(r);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grading failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onExplain = async (p, userAnswer) => {
    setExplainingId(p.id);
    try {
      const r = await aiExplain({
        worksheet_id: ws.id,
        problem_id: p.id,
        user_answer: userAnswer || undefined,
      });
      setExplanations((prev) => ({ ...prev, [p.id]: r.explanation }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI tutor is busy. Try again.");
    } finally {
      setExplainingId(null);
    }
  };

  const onAiHint = async (p) => {
    setHintLoadingId(p.id);
    try {
      const r = await aiHint({ worksheet_id: ws.id, problem_id: p.id });
      setAiHints((prev) => ({ ...prev, [p.id]: r.hint }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI tutor is busy. Try again.");
    } finally {
      setHintLoadingId(null);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
    setExplanations({});
    setAiHints({});
  };

  const onSubmitToTeacher = async () => {
    const email = teacherEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid teacher email address.");
      return;
    }
    setSending(true);
    try {
      // Rasterize the printed worksheet card — same look as the Print button.
      let pdf_base64 = null;
      try {
        if (printRef.current) pdf_base64 = await buildWorksheetPdfBase64(printRef.current);
      } catch (e) {
        pdf_base64 = null;
      }
      if (!pdf_base64) {
        toast.error("Couldn't render the worksheet PDF — please try again.");
        setSending(false);
        return;
      }
      await emailWorksheetToTeacher({
        teacher_email: email,
        worksheet: { worksheetTitle: ws.title },
        answers,
        meta,
        score: result ? `${result.score} / ${result.total}` : undefined,
        pdf_base64,
      });
      toast.success("Sent to your teacher!");
      setTeacherOpen(false);
      setTeacherEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again.");
    } finally {
      setSending(false);
    }
  };

  if (!ws) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>
        <Footer />
      </div>
    );
  }

  const aiAvailable = !!result;
  const sec = deterministicSection(ws.problems);
  const gradeLabel = ws.grade === "K" ? "Kindergarten" : `Grade ${ws.grade}`;

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
      <Header />
      <main className="max-w-4xl w-full mx-auto px-5 sm:px-8 py-10 flex-1 print:hidden">
        <div className="flex flex-wrap gap-3 justify-end mb-4" data-testid="worksheet-header">
          <button
            onClick={onPrint}
            className="brutal-btn bg-white text-[#0A0A0A] px-4 py-2 text-sm inline-flex items-center gap-2"
            data-testid="print-button"
          >
            <Printer strokeWidth={2.5} className="w-4 h-4" /> Print
          </button>
          <Link to={`/print/${ws.id}`} className="brutal-btn bg-white text-[#0A0A0A] px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="blank-print-button">
            <Printer strokeWidth={2.5} className="w-4 h-4" /> Blank
          </Link>
          <button onClick={reset} className="brutal-btn bg-brand-yellow px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="reset-button">
            <RefreshCw strokeWidth={2.5} className="w-4 h-4" /> Reset
          </button>
        </div>
        <WorksheetHeader
          title={ws.title}
          eyebrow="Worksheet"
          subtitle={`${gradeLabel} · ${ws.problems.length} ${ws.problems.length === 1 ? "item" : "items"}`}
        />

        {/* Interactive student header — typed directly; baked into the Print PDF and the teacher email */}
        <div className="mt-4 mb-6 grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm border-y-[3px] border-[#0A0A0A]/10 py-3">
          {[
            ["name", "Name"],
            ["date", "Date"],
            ["gradeSection", "Grade & Section"],
            ["subject", "Subject"],
          ].map(([k, label]) => (
            <label key={k} className="font-bold inline-flex items-baseline gap-2 min-w-0">
              <span className="shrink-0">{label}:</span>
              <input
                type="text"
                value={meta[k]}
                onChange={(e) => setMetaField(k, e.target.value)}
                className="flex-1 min-w-0 border-b-2 border-[#0A0A0A]/60 bg-transparent font-medium focus:outline-none focus:border-[#0A0A0A]"
                data-testid={`meta-${k}`}
              />
            </label>
          ))}
        </div>

        {result && (
          <div className="brutal-card p-6 sm:p-8 mb-8 bg-brand-blue text-white animate-pop-in" data-testid="score-banner">
            <p className="font-heading text-sm uppercase tracking-widest font-bold opacity-90">Your score</p>
            <div className="flex items-end gap-6 mt-2 flex-wrap">
              <div className="font-heading text-6xl font-bold">{result.percentage}%</div>
              <div className="font-heading text-2xl font-bold">{result.score}/{result.total} correct</div>
            </div>
            <p className="mt-3 text-sm">
              {result.percentage === 100
                ? "Perfect run. Want a harder set?"
                : result.percentage >= 80
                ? "Solid work. Tap any wrong answer for an AI walkthrough."
                : "Keep going — use the AI tutor on the ones you missed."}
            </p>
          </div>
        )}

        <SectionBanner index={0} title={sec.title} points={ws.problems.length} instructions={sec.instructions} />
        <div className="space-y-5" data-testid="problems-list">
          {ws.problems.map((p, i) => {
            const r = result?.results.find((x) => x.problem_id === p.id);
            const userVal = answers[p.id] || "";
            return (
              <div
                key={p.id}
                className={`brutal-card p-6 transition-all ${r ? (r.is_correct ? "bg-brand-green/15" : "bg-brand-coral/15") : "bg-white"}`}
                data-testid={`problem-${i + 1}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#0A0A0A] text-white rounded-xl flex items-center justify-center font-heading font-bold">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <ListenButton text={p.speak} />
                    <QuestionText text={p.question} />
                    <Figure svg={p.figure_svg} />
                    {p.choices ? (
                      <>
                        <MultipleChoice
                          choices={p.choices}
                          value={userVal}
                          onChange={(v) => setAnswers((a) => ({ ...a, [p.id]: v }))}
                          disabled={!!result}
                          result={r}
                        />
                        {r && (
                          <div className="mt-3 flex flex-wrap items-center gap-3">
                            <span className={`brutal-badge ${r.is_correct ? "bg-brand-green" : "bg-brand-coral"}`} data-testid={`result-${i + 1}`}>
                              {r.is_correct ? <><Check strokeWidth={3} className="w-3.5 h-3.5" /> Correct</> : <><X strokeWidth={3} className="w-3.5 h-3.5" /> {r.user_answer ? `You: ${r.user_answer} · ` : ""}Answer: <MathText>{r.correct_answer}</MathText></>}
                            </span>
                            <button
                              onClick={() => onExplain(p, userVal)}
                              disabled={explainingId === p.id || !aiAvailable}
                              title={!aiAvailable ? "Submit your answers first" : ""}
                              className="brutal-btn bg-brand-coral px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid={`explain-button-${i + 1}`}
                            >
                              {explainingId === p.id ? <Loader2 strokeWidth={3} className="w-3.5 h-3.5 animate-spin" /> : <BrainCircuit strokeWidth={3} className="w-3.5 h-3.5" />}
                              {!result ? "AI after submit" : "Explain with AI"}
                            </button>
                          </div>
                        )}
                        {!result && (
                          <div className="mt-3">
                            <button
                              onClick={() => onExplain(p, userVal)}
                              disabled={explainingId === p.id || !aiAvailable}
                              title={!aiAvailable ? "Submit your answers first" : ""}
                              className="brutal-btn bg-brand-coral px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                              data-testid={`explain-button-${i + 1}`}
                            >
                              {explainingId === p.id ? <Loader2 strokeWidth={3} className="w-3.5 h-3.5 animate-spin" /> : <BrainCircuit strokeWidth={3} className="w-3.5 h-3.5" />}
                              AI after submit
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <input
                        type="text"
                        value={userVal}
                        onChange={(e) => setAnswers((a) => ({ ...a, [p.id]: e.target.value }))}
                        disabled={!!result}
                        placeholder="Your answer"
                        className="brutal-input px-4 py-2.5 w-44 font-mono font-bold disabled:opacity-70 disabled:cursor-not-allowed"
                        data-testid={`answer-input-${i + 1}`}
                      />
                      {r && (
                        <span className={`brutal-badge ${r.is_correct ? "bg-brand-green" : "bg-brand-coral"}`} data-testid={`result-${i + 1}`}>
                          {r.is_correct ? <><Check strokeWidth={3} className="w-3.5 h-3.5" /> Correct</> : <><X strokeWidth={3} className="w-3.5 h-3.5" /> Answer: <MathText>{r.correct_answer}</MathText></>}
                        </span>
                      )}
                      <button
                        onClick={() => onExplain(p, userVal)}
                        disabled={explainingId === p.id || !aiAvailable}
                        title={!aiAvailable ? "Submit your answers first" : ""}
                        className="brutal-btn bg-brand-coral px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`explain-button-${i + 1}`}
                      >
                        {explainingId === p.id ? (
                          <Loader2 strokeWidth={3} className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <BrainCircuit strokeWidth={3} className="w-3.5 h-3.5" />
                        )}
                        {!result ? "AI after submit" : "Explain with AI"}
                      </button>
                    </div>
                    )}
                    <HintBlock
                      hint={p.hint}
                      aiHintText={aiHints[p.id]}
                      aiHintLoading={hintLoadingId === p.id}
                      onAskAi={() => onAiHint(p)}
                    />
                    <ReportProblem
                      question={p.question}
                      shownAnswer={r ? r.correct_answer : null}
                      worksheetId={ws.id}
                      problemId={p.id}
                      topic={ws.topic}
                    />
                    {explanations[p.id] && (
                      <div className="mt-4 p-4 bg-white border-[3px] border-[#0A0A0A] rounded-xl shadow-brutal-xs animate-pop-in" data-testid={`explanation-${i + 1}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles strokeWidth={2.5} className="w-4 h-4 text-brand-blue" />
                          <span className="font-heading text-xs uppercase tracking-widest font-bold">AI Tutor</span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          {(() => {
                            const raw = explanations[p.id];
                            // The advanced (grade 7+) format already puts each step on its own
                            // line; the simple K-6 How/Why/Conclusion format sometimes comes back
                            // on one line, so only fall back to inserting breaks when there's no
                            // real newline to split on.
                            const lines = (raw.includes("\n")
                              ? raw
                              : raw.replace(/(Why:|How:|Conclusion:|Step\s+\d+:|Answer:)/g, "\n$1")
                            ).split("\n").map(l => l.trim()).filter(l => l.length > 0);
                            return lines.map((line, li) => {
                              // Numbered step ("1. Check the Left-Hand Limit: ...") — bold just the
                              // step name so the reasoning sentence after it reads normally.
                              const numbered = /^(\d+\.\s*[^:]{1,60}:)\s*(.*)$/.exec(line);
                              if (numbered) {
                                const [, header, rest] = numbered;
                                return (
                                  <div key={li} className="text-sm font-body leading-relaxed mt-1">
                                    <span className="font-bold"><MathText>{header}</MathText></span>
                                    {rest && <> <MathText>{rest}</MathText></>}
                                  </div>
                                );
                              }
                              const isHeader = /^(Why:|How:|Conclusion:|Step\s+\d+:|Answer:)/.test(line);
                              return (
                                <div key={li} className={`text-sm font-body leading-relaxed${isHeader ? " font-bold mt-1" : ""}`}>
                                  <MathText>{line}</MathText>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <WorksheetFooter />

        {!result && (
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={onSubmit}
              disabled={submitting}
              className="brutal-btn bg-brand-blue text-white px-8 py-4 text-base inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {submitting ? <Loader2 strokeWidth={3} className="w-5 h-5 animate-spin" /> : <Check strokeWidth={3} className="w-5 h-5" />}
              {submitting ? "Grading..." : "Submit & Grade"}
            </button>
            <button
              type="button"
              onClick={() => setTeacherOpen(true)}
              className="brutal-btn bg-brand-green px-5 py-4 text-base inline-flex items-center gap-2"
              data-testid="submit-to-teacher"
            >
              <Send strokeWidth={3} className="w-4 h-4" /> Submit to Teacher
            </button>
          </div>
        )}

        {result && (
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/" className="brutal-btn bg-brand-yellow px-7 py-3.5" data-testid="another-button">Generate another</Link>
            <button
              type="button"
              onClick={() => setTeacherOpen(true)}
              className="brutal-btn bg-brand-green px-7 py-3.5 inline-flex items-center gap-2"
              data-testid="submit-to-teacher"
            >
              <Send strokeWidth={3} className="w-4 h-4" /> Submit to Teacher
            </button>
          </div>
        )}

        {/* Submit-to-Teacher modal */}
        {teacherOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 no-print"
            onClick={() => !sending && setTeacherOpen(false)}
          >
            <div
              className="w-full max-w-md bg-white border-[3px] border-[#0A0A0A] rounded-2xl shadow-brutal p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-heading text-xl font-bold inline-flex items-center gap-2">
                  <Mail strokeWidth={3} className="w-5 h-5" /> Submit to Teacher
                </h3>
                <button
                  type="button"
                  onClick={() => !sending && setTeacherOpen(false)}
                  aria-label="Close"
                  className="text-[#0A0A0A]/40 hover:text-brand-coral"
                >
                  <X strokeWidth={3} className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-[#0A0A0A]/60 mt-1">
                We'll compile your filled-out worksheet into a PDF and email it.
              </p>
              <label className="grid gap-1.5 mt-4">
                <span className="font-bold text-sm">Teacher's Email Address</span>
                <input
                  type="email"
                  value={teacherEmail}
                  onChange={(e) => setTeacherEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onSubmitToTeacher()}
                  placeholder="teacher@school.edu"
                  autoFocus
                  className="border-[3px] border-[#0A0A0A] rounded-xl px-3 py-2 font-medium shadow-brutal-xs focus:outline-none"
                  data-testid="teacher-email"
                />
              </label>
              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setTeacherOpen(false)}
                  disabled={sending}
                  className="px-4 py-2 text-sm font-bold rounded-full hover:bg-[#0A0A0A]/5 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSubmitToTeacher}
                  disabled={sending}
                  className="brutal-btn bg-brand-green px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="confirm-send-teacher"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send strokeWidth={3} className="w-4 h-4" />}
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          </div>
        )}

      </main>

      {/* Off-screen on the live page; becomes THE printed document via @media
          print (the interactive <main> above is print:hidden, this goes static).
          Also still rasterized by buildWorksheetPdfBase64 for the teacher email. */}
      <PracticePrintLayout printRef={printRef} ws={ws} answers={answers} result={result} meta={meta} />
      <Footer />
    </div>
  );
}
